// Average American grip strength baseline (combined male/female ~115 lbs)
const AVG_AMERICAN_GRIP_LBS = 115
// Average nerd time target: 45 seconds
const AVG_NERD_TIME_SECONDS = 45

export const calculateNerdScore = (timeSeconds: number): number => {
  // Score IS the time in seconds (lower = better for leaderboard)
  return Math.round(timeSeconds * 10) / 10
}

export const formatScore = (
  score: number,
  type: 'brawn' | 'brains',
): string => {
  if (type === 'brawn') return `${score} lbs`
  return `${score}s`
}

export interface IdentityResult {
  brutePercent: number
  nerdPercent: number
  label: string
  description: string
}

export function calculateBruteNerdIdentity(
  bruteLbs: number,
  nerdTimeSeconds: number,
): IdentityResult {
  // Normalize brute: ratio vs average American
  const normalizedBrute = Math.min(bruteLbs / AVG_AMERICAN_GRIP_LBS, 2.5)

  // Normalize nerd: faster = better. avgTime / playerTime (so faster player > 1.0)
  // Cap at 2.5 to prevent extreme scores dominating
  const normalizedNerd =
    nerdTimeSeconds > 0
      ? Math.min(AVG_NERD_TIME_SECONDS / nerdTimeSeconds, 2.5)
      : 0

  // Heavy brute weighting: 75% brute, 25% nerd
  const bruteWeighted = normalizedBrute * 0.75
  const nerdWeighted = normalizedNerd * 0.25

  const total = bruteWeighted + nerdWeighted
  const brutePercent =
    total === 0 ? 75 : Math.round((bruteWeighted / total) * 100)
  const nerdPercent = 100 - brutePercent

  let label: string
  let description: string

  if (brutePercent >= 90) {
    label = 'PURE BRUTE'
    description =
      'Your grip does the talking. The keyboard is just a paperweight.'
  } else if (brutePercent >= 78) {
    label = 'MOSTLY BRUTE'
    description =
      "Built for the physical world. You've got just enough smarts to be dangerous."
  } else if (brutePercent >= 65) {
    label = 'LEANING BRUTE'
    description =
      'More muscle than most, but you can hold your own in a board meeting.'
  } else if (brutePercent >= 50) {
    label = 'BALANCED (BRUTE EDGE)'
    description =
      'A rare blend — you could arm-wrestle and close the deal on the same day.'
  } else if (brutePercent >= 38) {
    label = 'LEANING NERD'
    description =
      "Your brain is your biggest muscle. But you're no pushover either."
  } else {
    label = 'CERTIFIED NERD'
    description = 'The spreadsheets fear you. The gym... not so much.'
  }

  return { brutePercent, nerdPercent, label, description }
}
